import {ProductEntryLink} from './product-entry';
import {useState, type KeyboardEvent} from 'react';
import {TEAM_ROLES} from '../src/team';
import {DEMOS} from './demo-catalog';
import {RoleIcon} from './role-icon';
import {APP_BASE, Link} from './router';
import {teamRolePath, teamWorkPath} from './public-paths';
import {PublicSectionRule} from './public-section-rule';
import './your-team.css';

export function YourTeamPage() {
  const [selected, setSelected] = useState(0);
  const role = TEAM_ROLES[selected]!;
  const demo = DEMOS[role.id];

  function selectWithKeyboard(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const next = event.key === 'ArrowRight' ? (index + 1) % TEAM_ROLES.length
      : event.key === 'ArrowLeft' ? (index + TEAM_ROLES.length - 1) % TEAM_ROLES.length
      : event.key === 'Home' ? 0 : event.key === 'End' ? TEAM_ROLES.length - 1 : null;
    if (next === null) return;
    event.preventDefault();
    setSelected(next);
    document.getElementById(`team-role-${TEAM_ROLES[next]!.id}`)?.focus();
  }

  return <div className="team-discovery">
    <header className="team-discovery-hero">
      <div><p className="team-discovery-label">Your team</p><h1>Turn your ideas<br/>into work,<br/>together.</h1></div>
      <div className="team-discovery-intro"><p>Give eight AI agents a goal. They share information, delegate tasks, and combine their skills to complete the work.</p>
        <p>You stay in the conversation. Ask a question or change the direction as your team works.</p>
        <div className="team-discovery-actions"><ProductEntryLink className="marketing-button" workspaceHref={`${APP_BASE}/workspace`}/>
          <Link className="team-whatsapp-link" href={`${APP_BASE}/settings?connect=whatsapp`}><img src={`${APP_BASE}/assets/whatsapp-official.svg`} width={26} height={26} alt=""/>Use WhatsApp</Link></div>
      </div>
    </header>

    <section className="team-explorer" aria-label="Explore the eight agents">
      <div className="team-role-tabs" role="tablist" aria-label="Choose an agent">{TEAM_ROLES.map((member, index) =>
        <button key={member.id} type="button" role="tab" id={`team-role-${member.id}`} aria-selected={selected === index}
          aria-controls="team-role-panel" tabIndex={selected === index ? 0 : -1}
          onClick={() => setSelected(index)} onKeyDown={event => selectWithKeyboard(event, index)}>
          <RoleIcon roleId={member.id} size={48}/><span>{member.name}</span>
        </button>)}</div>
      <div className="team-role-feature" role="tabpanel" id="team-role-panel" aria-labelledby={`team-role-${role.id}`} tabIndex={0}>
        <div className="team-feature-portrait"><RoleIcon roleId={role.id} size={240}/></div>
        <div className="team-feature-description"><p className="team-discovery-label">{role.discipline}</p><h2>{role.name}</h2>
          {role.id === 'head' ? <p>Give your team a goal. Head of Eight assigns the work, reviews the results, and brings the parts together.</p> : <p>{role.description}</p>}
          <Link className="team-detail-link" href={`${APP_BASE}${teamRolePath(role.id)}`}>Explore {role.name}</Link>
        </div>
        {demo && <div className="team-feature-work"><p className="team-discovery-label">Public work demo</p><h3>{demo.title}</h3>
          <ul>{demo.delivered.slice(0, 2).map(item => <li key={item}>{item}</li>)}</ul>
          <Link className="team-detail-link" href={`${APP_BASE}${teamWorkPath(role.id)}`}>View the work</Link>
        </div>}
      </div>
    </section>

    <section className="team-working-together" aria-labelledby="team-together-title"><PublicSectionRule/>
      <div><p className="team-discovery-label">Built to work together</p><h2 id="team-together-title">Start with a goal.<br/>Keep control of the work.</h2>
        <p>Head of Eight keeps the specialists working toward the same result. Each bot passes its findings and completed work back to the team.</p></div>
      <div className="team-work-principles">
        <article><h3>Let the team divide the task.</h3><p>Describe the result you need. Head of Eight assigns each part to the right specialist.</p></article>
        <article><h3>Speak to any bot.</h3><p>Open a specialist’s chat to ask a question, inspect its work, or give new instructions.</p></article>
        <article><h3>Build on what your team learns.</h3><p>Your team can remember work details and preferences across chats. Keep your files and conversations in the same workspace.</p></article>
      </div>
    </section>
    <section className="team-discovery-closing"><PublicSectionRule/><h2>Give your team<br/>its next task.</h2><div><p>Start in your browser. Connect WhatsApp to send tasks and read replies from your phone.</p>
      <div className="team-discovery-actions"><ProductEntryLink className="marketing-button" workspaceHref={`${APP_BASE}/workspace`}/>
        <Link className="team-whatsapp-link" href={`${APP_BASE}/settings?connect=whatsapp`}><img src={`${APP_BASE}/assets/whatsapp-official.svg`} width={26} height={26} alt=""/>Use WhatsApp</Link></div></div></section>
  </div>;
}
